import { motion } from 'framer-motion';
import { message } from 'antd';
import {
  FileTextOutlined,
  FilePdfOutlined,
  FileExcelOutlined,
  FileWordOutlined,
  FileOutlined,
} from '@ant-design/icons';
import { useCallback, useMemo } from 'react';
import { Role } from '../../domain/models';
import styles from './Chat.module.scss';
import LoadingDots from '../../components/LoadingDots/LoadingDots';
import MarkdownMessage from './MarkdownMessage';
import { copyToClipboard } from '../../utils/clipboard';
import { speakText } from '../../utils/textToSpeech';
import ChatMessageActions, { createCopyAction, createSpeakAction } from './actions/ChatMessageActions';
import MessageFeedback from './feedback/MessageFeedback';

export interface ChatMessageBubbleProps {
  id: string;
  role: Role;
  content: string;
  isLatest?: boolean;
  isSending?: boolean;
  attachments?: string[];
  tenantId?: string;
  threadId?: string;
}

const getFileIcon = (fileName: string) => {
  const ext = fileName.split('.').pop()?.toLowerCase();
  if (ext === 'pdf') return <FilePdfOutlined className={styles.bubbleFileIcon} />;
  if (ext === 'xlsx' || ext === 'csv') return <FileExcelOutlined className={styles.bubbleFileIcon} />;
  if (ext === 'docx') return <FileWordOutlined className={styles.bubbleFileIcon} />;
  if (ext === 'txt') return <FileTextOutlined className={styles.bubbleFileIcon} />;
  return <FileOutlined className={styles.bubbleFileIcon} />;
};

const ChatMessageBubble = ({
  id,
  role,
  content,
  isLatest,
  isSending,
  attachments,
  tenantId,
  threadId,
}: ChatMessageBubbleProps) => {
  const containerClass = role === 'user' ? styles.messageContainerUser : styles.messageContainerSystem;
  const bubbleClass = role === 'user' ? styles.messageBubbleUser : styles.messageBubbleSystem;

  const handleCopy = useCallback(async () => {
    if (!content) return;
    try {
      await copyToClipboard(content);
      message.success('Message copied');
    } catch (err) {
      console.error('Failed to copy message', err);
      message.error('Unable to copy message');
    }
  }, [content]);

  const handleSpeak = useCallback(async () => {
    if (!content) return;
    try {
      await speakText(content);
      message.info('Playing message audio');
    } catch (err) {
      console.error('Failed to speak message', err);
      message.error('Unable to play message audio');
    }
  }, [content]);

  const systemActions = useMemo(() => {
    if (!content) return [];
    return [createCopyAction(handleCopy), createSpeakAction(handleSpeak)];
  }, [content, handleCopy, handleSpeak]);

  // Show loading dots if:
  // - This is a system message (AI response)
  // - It's the latest message
  // - We're currently sending (waiting for AI response)
  // - The content is empty (response hasn't started streaming yet)
  const showLoadingDots = role === 'system' && isLatest && isSending && !content;

  const renderBubbleContent = () => {
    if (showLoadingDots) {
      return <LoadingDots message="Rubix is thinking..." />;
    }
    if (role === 'system') {
      return <MarkdownMessage content={content} streaming={isSending && isLatest} />;
    }
    return (
      <>
        {attachments && attachments.length > 0 && (
          <div className={styles.bubbleAttachments}>
            {attachments.map(name => {
              const ext = name.split('.').pop()?.toUpperCase() ?? 'FILE';
              const baseName = name.replace(/\.[^.]+$/, '');
              return (
                <div key={name} className={styles.bubbleFileCard}>
                  <div className={styles.bubbleFileIconWrap}>
                    {getFileIcon(name)}
                  </div>
                  <div className={styles.bubbleFileInfo}>
                    <span className={styles.bubbleFileName} title={name}>{baseName}</span>
                    <span className={styles.bubbleFileExt}>{ext}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {content}
      </>
    );
  };

  return (
    <motion.div
      key={id}
      layout
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className={containerClass}
    >
      <div className={bubbleClass}>
        {renderBubbleContent()}
      </div>
      {!showLoadingDots && role === 'system' ? (
        <div className={styles.messageActionsRow}>
          <ChatMessageActions actions={systemActions} />
          {tenantId && threadId && (
            <MessageFeedback tenantId={tenantId} threadId={threadId} messageId={id} />
          )}
        </div>
      ) : null}
    </motion.div>
  );
};

export default ChatMessageBubble;
